import { TypedDocumentNode, gql } from '@apollo/client';

// eslint-disable-next-line import/named
import { createOrUpdateSo5LineupInput } from '@sorare/core/src/__generated__/globalTypes';
import {
  Level,
  useSnackNotificationContext,
} from '@sorare/core/src/contexts/snackNotification';
import { formatGqlErrors } from '@sorare/core/src/gql';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';

import { HANDLED_RULES } from 'lib/so5';

import {
  CreateOrUpdateSo5LineupMutation,
  CreateOrUpdateSo5LineupMutationVariables,
} from './__generated__/useCreateOrUpdateLineup.graphql';

type CreateOrUpdateSo5LineupMutation_createOrUpdateSo5Lineup_errors =
  NonNullable<
    CreateOrUpdateSo5LineupMutation['createOrUpdateSo5Lineup']
  >['errors'][number];

const CREATE_OR_UPDATE_SO5_LINEUP_MUTATION = gql`
  mutation CreateOrUpdateSo5LineupMutation(
    $input: createOrUpdateSo5LineupInput!
    $so5LeaderboardSlug: String!
  ) {
    createOrUpdateSo5Lineup(input: $input) {
      so5Lineup {
        id
        so5Appearances {
          id
          anyCard {
            slug
            concurrentSo5Lineups(so5LeaderboardSlug: $so5LeaderboardSlug) {
              id
            }
          }
        }
      }
      so5LeaderboardContender {
        slug
        so5Leaderboard {
          slug
          tasks {
            ... on ManagerProgressionTask {
              id
              probabilisticBundles {
                id
                opened
              }
            }
          }
          so5Fixture {
            slug
            so5LeaderboardGroups {
              slug
              eligibleCardsCountByPosition {
                slug
                position
                seasonality
                totalCount
                usedCardsCount
              }
              # Update the cache for the lobby to add the newly created team slot
              # and make the scroll to that team work post compose team
              mySo5LeaderboardContenders {
                slug
              }
            }
          }
        }
      }
      currentUser {
        slug
        forcedIntoOnboarding
        blockchainCardsInLineups
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  CreateOrUpdateSo5LineupMutation,
  CreateOrUpdateSo5LineupMutationVariables
>;

export default ({
  so5LeaderboardSlug,
  hideNotification = false,
}: {
  so5LeaderboardSlug: string;
  hideNotification?: boolean;
}) => {
  const [createOrUpdateSo5Lineup] = useMutation(
    CREATE_OR_UPDATE_SO5_LINEUP_MUTATION,
    { showErrorsWithSnackNotification: true }
  );

  const { showNotification } = useSnackNotificationContext();

  return async (input: createOrUpdateSo5LineupInput) => {
    const result = await createOrUpdateSo5Lineup({
      variables: { input, so5LeaderboardSlug },
    }).catch(e => {
      return {
        data: {
          createOrUpdateSo5Lineup: {
            so5Lineup: null,
            so5LeaderboardContender: null,
            errors: [e],
          },
        },
      };
    });
    const errors = result.data?.createOrUpdateSo5Lineup?.errors || [];
    if (!errors?.length) {
      if (!hideNotification) {
        showNotification('composeLineup');
      }
      return {
        data: result.data,
      };
    }

    const { unhandledErrors, handledErrors } = errors.reduce<{
      unhandledErrors: CreateOrUpdateSo5LineupMutation_createOrUpdateSo5Lineup_errors[];
      handledErrors: CreateOrUpdateSo5LineupMutation_createOrUpdateSo5Lineup_errors[];
    }>(
      (prev, curr) => {
        const ruleHandled = HANDLED_RULES.find(rule => {
          return rule.toLowerCase() === curr.path?.[1].toLowerCase();
        });
        if (ruleHandled) {
          prev.handledErrors.push(curr);
        } else {
          prev.unhandledErrors.push(curr);
        }
        return prev;
      },
      { unhandledErrors: [], handledErrors: [] }
    );
    if (unhandledErrors?.length) {
      showNotification(
        'errors',
        { errors: formatGqlErrors(unhandledErrors) },
        { level: Level.WARN }
      );
    }
    return { unhandledErrors, handledErrors };
  };
};
